import { useFormContext } from 'react-hook-form';
import React, { useMemo } from 'react';
import { khachHangSearchColumns, phuongTienVanChuyenSearchColumns, QUERY_KEYS } from '@/constants';
import type { KhachHang, PhuongTienVanChuyen } from '@/types/schemas';
import { FormField, SearchField } from '@/components/custom/erp';
import { FormFieldActions, FormFieldState } from '../../hooks';
import { usePhuongThucThanhToan } from '@/hooks';
import { Label } from '@/components/ui/label';
import { FormMode } from '@/types/form';

interface OtherTabProps {
  formMode: FormMode;
  formState: {
    state: FormFieldState;
    actions: FormFieldActions;
  };
}

export const OtherTab = ({ formMode, formState: { state, actions } }: OtherTabProps) => {
  const isDisabled = formMode === 'view';
  const { paymentMethods } = usePhuongThucThanhToan();
  const { setValue } = useFormContext();
  const paymentMethodsOpts = useMemo(() => {
    return paymentMethods.map(paymentMethod => ({
      value: paymentMethod.uuid,
      label: paymentMethod.ten_pttt
    }));
  }, [paymentMethods]);
  setValue('ma_pttt', paymentMethodsOpts[0]?.value || '');

  return (
    <div className='space-y-1 p-4'>
      {/* Row 1: Trạng thái HĐDT (left) + Số chứng từ (right) */}
      <div className='grid grid-cols-10 gap-4'>
        <div className='col-span-3 flex items-center gap-2'>
          <Label className='w-32 min-w-32 text-left'>Trạng thái HĐDT</Label>
          <div className='flex-1'>
            <FormField
              name='ma_tthddt'
              type='select'
              disabled={isDisabled}
              options={[
                { value: '0', label: 'Không sử dụng' },
                { value: '1', label: 'Chờ phát hành' }
              ]}
              className='w-[200px]'
            />
          </div>
        </div>

        <div className='col-span-5 flex items-center gap-2'>
          <Label className='w-28 min-w-28 text-left'>Số chứng từ</Label>
          <div className='flex-1'>
            <FormField name='so_ct_hddt' type='text' disabled={true} className='w-[200px]' />
          </div>
        </div>
      </div>

      {/* Row 2: Thanh toán (left) + Ngày hóa đơn (right) */}
      <div className='grid grid-cols-10 gap-4'>
        <div className='col-span-3 flex items-center gap-2'>
          <Label className='w-32 min-w-32 text-left'>Thanh toán</Label>
          <div className='flex-1'>
            <FormField
              name='ma_pttt'
              type='select'
              disabled={isDisabled}
              options={paymentMethodsOpts}
              value={typeof state.paymentMethod === 'string' ? state.paymentMethod : state.paymentMethod?.uuid || ''}
              className='w-[200px]'
            />
          </div>
        </div>

        <div className='col-span-5 flex items-center gap-2'>
          <Label className='w-28 min-w-28 text-left'>Ngày hóa đơn</Label>
          <div className='flex-1'>
            <FormField name='ngay_ct_hddt' type='date' disabled={isDisabled} className='w-[200px]' />
          </div>
        </div>
      </div>

      {/* Row 3: Lệnh điều động (left) + Ký hiệu (right) */}
      <div className='grid grid-cols-10 gap-4'>
        <div className='col-span-3 flex items-center gap-2'>
          <Label className='w-32 min-w-32 text-left'>Lệnh điều động</Label>
          <div className='flex-1'>
            <FormField name='lenh_dd' type='text' disabled={isDisabled} className='w-[200px]' />
          </div>
        </div>

        <div className='col-span-5 flex items-center gap-2'>
          <Label className='w-28 min-w-28 text-left'>Ký hiệu</Label>
          <div className='flex-1'>
            <FormField name='so_ct2_hddt' type='text' disabled={true} className='w-[200px]' />
          </div>
        </div>
      </div>

      {/* Row 4: Ngày điều động (left) + Mẫu hóa đơn (right) */}
      <div className='grid grid-cols-10 gap-4'>
        <div className='col-span-3 flex items-center gap-2'>
          <Label className='w-32 min-w-32 text-left'>Ngày điều động</Label>
          <div className='flex-1'>
            <FormField name='ngay_dd' type='date' disabled={isDisabled} className='w-[200px]' />
          </div>
        </div>

        <div className='col-span-5 flex items-center gap-2'>
          <Label className='w-28 min-w-28 text-left'>Mẫu hóa đơn</Label>
          <div className='flex-1'>
            <FormField name='ma_mau_ct_hddt' type='text' disabled={true} className='w-[200px]' />
          </div>
        </div>
      </div>

      {/* Row 5: Người điều động (left) + Về việc (right) */}
      <div className='grid grid-cols-10 gap-4'>
        <div className='col-span-2 flex items-center gap-2'>
          <Label className='w-32 min-w-32 text-left'>Người điều động</Label>
          <div className='flex-1'>
            <FormField name='nguoi_dd' type='text' disabled={isDisabled} className='w-[200px]' />
          </div>
        </div>

        <div className='col-span-5 ml-4 flex items-center gap-2'>
          <Label className='w-14 min-w-14 text-left'>Về việc</Label>
          <div className='flex-1'>
            <FormField name='viec_dd' type='text' disabled={isDisabled} className='w-[300px]' />
          </div>
        </div>
      </div>

      {/* Row 6: Khách hàng v/c (left) - SearchField */}
      <div className='grid grid-cols-10 gap-4'>
        <div className='col-span-5 flex items-center gap-2'>
          <Label className='w-32 min-w-32 text-left'>Khách hàng v/c</Label>
          <SearchField<KhachHang>
            name='ma_kh_vc'
            searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}`}
            searchColumns={khachHangSearchColumns}
            dialogTitle='Chọn khách hàng'
            columnDisplay='customer_code'
            displayRelatedField='customer_name'
            value={state.selectedCustomer?.customer_code || ''}
            relatedFieldValue={state.selectedCustomer?.customer_name || ''}
            onRowSelection={actions.setSelectedCustomer}
            disabled={isDisabled}
          />
        </div>
      </div>

      {/* Row 7: Tên khách hàng v/c (left) */}
      <div className='grid grid-cols-1 gap-4'>
        <div className='flex items-center gap-2'>
          <Label className='w-32 min-w-32 text-left'>Tên khách hàng v/c</Label>
          <div className='flex-1'>
            <FormField
              name='ten_kh_vc'
              type='text'
              value={state.selectedCustomer?.customer_name}
              disabled={isDisabled}
            />
          </div>
        </div>
      </div>

      {/* Row 8: Số hợp đồng v/c (left) */}
      <div className='grid grid-cols-10 gap-4'>
        <div className='col-span-5 flex items-center gap-2'>
          <Label className='w-32 min-w-32 text-left'>Số hợp đồng v/c</Label>
          <div className='flex-1'>
            <FormField name='so_hd_vc' type='text' disabled={isDisabled} className='w-[200px]' />
          </div>
        </div>
      </div>

      {/* Row 9: Phương tiện v/c (left) - SearchField */}
      <div className='grid grid-cols-10 gap-4'>
        <div className='col-span-5 flex items-center gap-2'>
          <Label className='w-32 min-w-32 text-left'>Phương tiện v/c</Label>
          <SearchField<PhuongTienVanChuyen>
            name='ma_ptvc'
            searchEndpoint={`/${QUERY_KEYS.PHUONG_TIEN_VAN_CHUYEN}`}
            searchColumns={phuongTienVanChuyenSearchColumns}
            dialogTitle='Chọn phương tiện'
            columnDisplay='ma_ptvc'
            displayRelatedField='ten_ptvc'
            value={state.selectedVehicle?.ma_ptvc}
            relatedFieldValue={state.selectedVehicle?.ten_ptvc}
            onRowSelection={actions.setSelectedVehicle}
            disabled={isDisabled}
          />
        </div>
      </div>

      {/* Row 10: Lý do hủy (left) */}
      <div className='grid grid-cols-1 gap-4'>
        <div className='col-span-5 flex items-center gap-2'>
          <Label className='w-32 min-w-32 text-left'>Lý do hủy</Label>
          <div className='flex-1'>
            <FormField name='ly_do_huy' type='text' disabled={isDisabled} />
          </div>
        </div>
      </div>

      {/* Row 11: Lý do điều chỉnh (full width) */}
      <div className='grid grid-cols-1 gap-4'>
        <div className='flex items-center gap-2'>
          <Label className='w-32 min-w-32 text-left'>Lý do điều chỉnh</Label>
          <div className='flex-1'>
            <FormField
              name='ly_do'
              type='text'
              disabled={isDisabled}
              placeholder='Nhập lý do thay thế hoặc điều chỉnh hóa đơn điện tử'
            />
          </div>
        </div>
      </div>
    </div>
  );
};
